import { Controller } from '@hotwired/stimulus';
import { readQrCode, stopScan } from '../js/qr_reader';

// Distribution à un membre : la caméra de l'étape affichée
// (cible `video`, remplacée à chaque étape par le serveur) lit un code, qui
// part par le formulaire caché de l'étape (`scanForm`, champ `code`) ; la
// réponse (Turbo Stream) remplace l'étape, ajoute une ligne à la liste ou
// affiche le motif d'un refus (cible `error`). Ici : caméra, « Scanner à
// nouveau », doublon d'un équipement déjà listé, retrait d'une ligne, bouton
// de validation actif dès qu'une ligne est listée, motif d'un envoi qui n'a
// pas atteint le serveur. La caméra est arrêtée en quittant l'étape ou la
// page, et n'est pas demandée par l'aperçu Turbo.
export default class extends Controller {
    static targets = ['video', 'message', 'rescan', 'scanForm', 'code', 'error', 'item', 'submit', 'empty'];

    disconnect() {
        this.stop();
    }

    videoTargetConnected() {
        if (document.documentElement.hasAttribute('data-turbo-preview')) return;
        this.read();
    }

    videoTargetDisconnected(video) {
        if (this.video === video) this.stop();
    }

    itemTargetConnected() {
        this.refresh();
    }

    itemTargetDisconnected() {
        this.refresh();
    }

    rescan() {
        this.read();
    }

    async read() {
        this.stop();
        if (!this.hasVideoTarget) return;
        const video = this.videoTarget;
        this.video = video;
        video.hidden = false;
        if (this.hasRescanTarget) this.rescanTarget.hidden = true;
        if (this.hasMessageTarget) this.messageTarget.textContent = '';
        const scan = { isCurrent: () => this.current === scan };
        this.current = scan;
        let text;
        try {
            text = await readQrCode(video, scan);
        } catch (error) {
            if (this.current !== scan) return;
            video.hidden = true;
            if (this.hasRescanTarget) this.rescanTarget.hidden = false;
            if (this.hasMessageTarget) this.messageTarget.textContent = 'Caméra indisponible : autorisez la caméra pour cette page.';
            return;
        }
        if (this.current !== scan) return;
        this.found(text);
    }

    // Code lu (caméra, ou appelé directement) : envoyé au serveur, sauf un
    // équipement déjà listé.
    found(text) {
        if (!this.hasScanFormTarget) return;
        this.stop();
        if (this.hasVideoTarget) this.videoTarget.hidden = true;
        if (this.hasRescanTarget) this.rescanTarget.hidden = false;
        if (this.itemTargets.some((item) => item.dataset.qrCode === text)) {
            this.showError('Équipement déjà scanné.');
            return;
        }
        this.showError('');
        this.codeTarget.value = text;
        this.scanFormTarget.requestSubmit();
    }

    // Envoi d'un formulaire de l'étape (nom choisi, validation) : motif
    // précédent effacé, caméra arrêtée pour la validation.
    submitting({ target }) {
        this.showError('');
        if (target.querySelector('[name="equip_items[]"]')) this.stop();
    }

    // Envoi sans réponse (réseau) : lecture d'un code ou validation de la liste.
    failed({ target }) {
        const validation = target instanceof HTMLFormElement && target.querySelector('[name="equip_items[]"]');
        this.showError(validation ? 'Validation impossible, réessayez plus tard.' : 'Lecture impossible, réessayez plus tard.');
    }

    remove({ currentTarget }) {
        currentTarget.closest('li')?.remove();
    }

    refresh() {
        const count = this.itemTargets.length;
        if (this.hasSubmitTarget) this.submitTarget.disabled = count === 0;
        if (this.hasEmptyTarget) this.emptyTarget.hidden = count > 0;
    }

    showError(message) {
        if (!this.hasErrorTarget) return;
        this.errorTarget.textContent = message;
        this.errorTarget.hidden = message === '';
    }

    stop() {
        if (this.video) stopScan(this.current, this.video);
        this.current = null;
    }
}
